import { liteClient as algoliasearch } from 'algoliasearch/lite';
import aa from 'search-insights';

// Escape plain text/attribute values. Algolia's `_highlightResult.*.value`
// fields are pre-escaped markup (with <em> match tags) and are rendered raw on
// purpose; everything else from the index is escaped through this.
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[c]));

// Algolia config rides on the search input as data-* (rendered only on support
// pages) rather than window globals in every page's <head>.
const searchInputs = document.querySelectorAll('[data-support-search]');
const cfg = searchInputs.length ? searchInputs[0].dataset : {};
const APP_ID = cfg.algoliaApp;
const SEARCH_KEY = cfg.algoliaKey;
const INDEX_NAME = cfg.algoliaIndex;

if (searchInputs.length && (!APP_ID || !SEARCH_KEY)) {
  console.warn('[support-search] Algolia config missing — search disabled');
} else if (APP_ID && SEARCH_KEY) {
  const client = algoliasearch(APP_ID, SEARCH_KEY);

  aa('init', { appId: APP_ID, apiKey: SEARCH_KEY, useCookie: true });

  // Keep the anonymous user token in sync so searches and click events are
  // attributed to the same user — required for Click Analytics + Dynamic Re-Ranking.
  let userToken;
  aa('onUserTokenChange', (token) => { userToken = token; }, { immediate: true });

  let queryToken = null;

  searchInputs.forEach(initInput);

  function initInput(input) {
    const panel = input.parentElement.querySelector('[data-support-search-panel]');
    if (!panel) return;

    const statusEl = panel.querySelector('.support-search-panel__status');
    const resultsEl = panel.querySelector('.support-search-panel__results');
    const scope = input.dataset.scopeCategory;

    let activeIdx = -1;
    let lastResults = [];
    let timer = null;

    const close = () => {
      panel.hidden = true;
      activeIdx = -1;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      document.body.classList.remove('support-search-open');
    };

    const open = () => {
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      document.body.classList.add('support-search-open');
    };

    // Point the input's aria-activedescendant at the active option so screen
    // readers announce the highlighted result during arrow-key navigation.
    const syncActiveDescendant = () => {
      if (activeIdx >= 0 && lastResults.length) {
        input.setAttribute('aria-activedescendant', 'support-result-' + activeIdx);
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    };

    const render = (hits, query) => {
      lastResults = hits;
      activeIdx = hits.length ? 0 : -1;
      resultsEl.innerHTML = hits.map((h, i) => `
        <li role="option" id="support-result-${i}" class="support-search-result ${i === activeIdx ? 'is-active' : ''}" data-url="${esc(h.url)}">
          <span class="support-search-result__cat">${esc(h.categoryName)}</span>
          <span class="support-search-result__title">${h._highlightResult?.title?.value || esc(h.title)}</span>
          <span class="support-search-result__summary">${h._highlightResult?.summary?.value || esc(h.summary)}</span>
        </li>
      `).join('');
      statusEl.textContent = hits.length
        ? `${hits.length} result${hits.length === 1 ? '' : 's'} for "${query}"`
        : `No results for "${query}"`;
      syncActiveDescendant();

      resultsEl.querySelectorAll('.support-search-result').forEach((el, i) => {
        el.addEventListener('mouseenter', () => { activeIdx = i; updateActive(); });
        el.addEventListener('click', () => navigateToResult(i, query));
      });
    };

    const updateActive = () => {
      resultsEl.querySelectorAll('.support-search-result').forEach((el, i) => {
        el.classList.toggle('is-active', i === activeIdx);
      });
      syncActiveDescendant();
    };

    const navigateToResult = (i, query) => {
      const hit = lastResults[i];
      if (!hit) return;
      // Algolia Insights: clicked-after-search.
      try {
        aa('clickedObjectIDsAfterSearch', {
          eventName: 'support_search_click',
          index: INDEX_NAME,
          queryID: queryToken,
          objectIDs: [String(hit.objectID)],
          positions: [i + 1],
        });
      } catch (_) {}
      window.location.href = hit.url;
    };

    let seq = 0; // guards against out-of-order responses overwriting newer ones
    const search = async (query) => {
      const reqId = ++seq;
      if (!query || query.length < 2) {
        resultsEl.innerHTML = '';
        statusEl.textContent = 'Type to search';
        lastResults = [];
        activeIdx = -1;
        syncActiveDescendant();
        return;
      }

      const params = { query, hitsPerPage: 8, clickAnalytics: true };
      if (userToken) params.userToken = userToken;
      if (scope) params.filters = `categorySlug:${scope}`;

      try {
        const res = await client.search({
          requests: [{ indexName: INDEX_NAME, ...params }],
        });
        if (reqId !== seq) return; // a newer query has since been issued
        const result = res.results[0];
        queryToken = result.queryID;
        render(result.hits, query);
      } catch (e) {
        if (reqId !== seq) return;
        statusEl.textContent = 'Search failed — try again.';
        console.error(e);
      }
    };

    input.addEventListener('input', (e) => {
      const q = e.target.value.trim();
      open();
      clearTimeout(timer);
      timer = setTimeout(() => search(q), 120);
    });

    input.addEventListener('focus', () => {
      if (input.value.trim()) open();
    });

    input.addEventListener('keydown', (e) => {
      if (panel.hidden) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); activeIdx = Math.min(activeIdx + 1, lastResults.length - 1); updateActive(); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); activeIdx = Math.max(activeIdx - 1, 0); updateActive(); }
      if (e.key === 'Enter' && activeIdx >= 0) {
        e.preventDefault();
        navigateToResult(activeIdx, input.value.trim());
      }
      if (e.key === 'Escape') { close(); input.blur(); }
    });

    // Close on outside click.
    document.addEventListener('click', (e) => {
      if (!input.parentElement.contains(e.target)) close();
    });

    // Cmd/Ctrl-K focuses the first search input on /support/*.
    document.addEventListener('keydown', (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        const first = document.querySelector('[data-support-search]');
        if (first) { e.preventDefault(); first.focus(); }
      }
    });
  }
}
